import React from 'react';
import { Link } from 'react-router-dom';
import { Tabs, Table, Button, Modal, Form, Select, InputNumber, DatePicker, Input, Checkbox, notification, Typography, Space, Tag, Tooltip, Popconfirm } from 'antd';
import { PlusOutlined, SendOutlined, CheckOutlined, LeftOutlined, RightOutlined, SaveOutlined, LockOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

import { label, money, number, date as fmtDate } from '../../lib/format';
const STATUS_COLORS = { DRAFT: 'default', SUBMITTED: 'blue', APPROVED: 'green' };
const fmt = (n) => (n ? Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');

/**
 * Weekly timesheet grid (xlsx "Timesheet": one row per engagement, one column
 * per day, "Total hrs"). Cells are the ordinary timesheet entries; submitted /
 * approved cells are locked.
 */
function WeeklyGrid({ companyId, users, isManager, auth }) {
  const [date, setDate] = React.useState(moment());
  const [userId, setUserId] = React.useState(auth.user.id);
  const [grid, setGrid] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [dirty, setDirty] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [adding, setAdding] = React.useState(null); // engagement id (or 'internal') chosen in the add-row select

  const load = React.useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    request
      .get('/timesheets/week', { company_id: companyId, user_id: userId, date: date.format('YYYY-MM-DD') })
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        setGrid(r.data);
        setRows(r.data.rows.map((row) => ({ ...row, hours: Object.fromEntries(r.data.days.map((d) => [d.date, row.cells[d.date] ? row.cells[d.date].hours : null])) })));
        setDirty(false);
      })
      .finally(() => setLoading(false));
  }, [companyId, userId, date]);
  React.useEffect(load, [load]);

  const setCell = (key, day, value) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, hours: { ...r.hours, [day]: value } } : r)));
    setDirty(true);
  };
  const addRow = () => {
    if (!adding) return;
    const opt = adding === 'internal' ? { id: null, client_id: null, service_id: null, label: 'Internal / administration' } : grid.engagements.find((e) => e.id === adding);
    const key = opt.id ? `e${opt.id}` : 'c0-s0';
    if (rows.some((r) => r.key === key)) return notification.info({ message: 'That row is already on the sheet.' });
    setRows((rs) => [...rs, { key, engagement_id: opt.id, client_id: opt.client_id, service_id: opt.service_id, label: opt.label, cells: {}, hours: {}, total: 0, billable: !!opt.id }]);
    setAdding(null);
    setDirty(true);
  };
  const removeRow = (key) => {
    // clearing the draft cells removes the entries on save; locked cells stay
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, hours: Object.fromEntries(grid.days.map((d) => [d.date, r.cells[d.date] && r.cells[d.date].locked ? r.cells[d.date].hours : 0])), removed: true } : r)));
    setDirty(true);
  };
  const save = () => {
    const payload = rows.map((r) => ({ engagement_id: r.engagement_id, client_id: r.client_id, service_id: r.service_id, description: r.description || null, billable: r.billable, hours: Object.fromEntries(grid.days.map((d) => [d.date, r.hours[d.date] || 0])) }));
    request
      .put('/timesheets/week', { company_id: companyId, user_id: userId, week_start: grid.week_start, rows: payload })
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        notification.success({ message: 'Week saved', description: r.message });
        load();
      });
  };
  const submit = () =>
    request
      .post('/timesheets/week/submit', { company_id: companyId, user_id: userId, week_start: grid.week_start })
      .then((r) => r.json())
      .then((r) => {
        if (!r.status) return notification.error({ message: r.message });
        notification.success({ message: r.message });
        load();
      });

  const rowTotal = (r) => Object.values(r.hours).reduce((s, v) => s + (Number(v) || 0), 0);
  const dayTotal = (d) => rows.reduce((s, r) => s + (Number(r.hours[d]) || 0), 0);
  const visibleRows = rows.filter((r) => !r.removed);
  const weekTotal = visibleRows.reduce((s, r) => s + rowTotal(r), 0);
  const usedKeys = new Set(visibleRows.map((r) => r.key));
  const addOptions = grid
    ? [{ value: 'internal', label: 'Internal / administration (no engagement)', disabled: usedKeys.has('c0-s0') }, ...grid.engagements.map((e) => ({ value: e.id, label: e.label, disabled: usedKeys.has(`e${e.id}`) }))]
    : [];

  const columns = grid
    ? [
        {
          title: 'Engagement',
          key: 'label',
          fixed: 'left',
          width: 260,
          render: (_, r) => (
            <span>
              {r.label}
              {r.billable === false && <Tag style={{ marginLeft: 6 }}>non-billable</Tag>}
            </span>
          ),
        },
        ...grid.days.map((d) => ({
          title: (
            <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
              <div>{moment(d.date).format('ddd')}</div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>{moment(d.date).format('DD/MM')}</Typography.Text>
              {d.holiday && (
                <div>
                  <Tooltip title={d.holiday.name}>
                    <Tag color={d.holiday.type === 'PUBLIC' ? 'red' : 'orange'} style={{ fontSize: 10, lineHeight: '16px', marginRight: 0 }}>{d.holiday.type === 'PUBLIC' ? 'Public hol.' : 'Office hol.'}</Tag>
                  </Tooltip>
                </div>
              )}
            </div>
          ),
          key: d.date,
          width: 96,
          align: 'center',
          onCell: () => ({ style: { background: d.working ? undefined : '#fafafa' } }),
          onHeaderCell: () => ({ style: { background: d.working ? undefined : '#f5f5f5' } }),
          render: (_, r) => {
            const cell = r.cells[d.date];
            if (cell && cell.locked) {
              return (
                <Tooltip title={`${cell.status.toLowerCase()} - cannot be changed here`}>
                  <span>
                    {fmt(cell.hours)} <LockOutlined style={{ color: cell.status === 'APPROVED' ? '#389e0d' : '#1677ff' }} />
                  </span>
                </Tooltip>
              );
            }
            return <InputNumber size="small" min={0} max={24} step={0.25} controls={false} style={{ width: 70 }} value={r.hours[d.date]} onChange={(v) => setCell(r.key, d.date, v)} />;
          },
        })),
        { title: 'Total hrs', key: 'total', width: 90, align: 'right', render: (_, r) => <strong>{fmt(rowTotal(r)) || '0.00'}</strong> },
        {
          title: '',
          key: 'x',
          width: 40,
          render: (_, r) => (
            <Popconfirm title="Clear this row's draft hours?" onConfirm={() => removeRow(r.key)}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ),
        },
      ]
    : [];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button icon={<LeftOutlined />} onClick={() => setDate((d) => d.clone().subtract(7, 'days'))} />
        <DatePicker picker="week" value={date} onChange={(v) => v && setDate(v)} allowClear={false} format={grid ? `[Week of ${moment(grid.week_start).format('DD MMM YYYY')}]` : 'wo'} style={{ width: 220 }} />
        <Button icon={<RightOutlined />} onClick={() => setDate((d) => d.clone().add(7, 'days'))} />
        <Button onClick={() => setDate(moment())}>This week</Button>
        {isManager && <Select showSearch optionFilterProp="label" style={{ width: 220 }} value={userId} onChange={setUserId} options={users.map((u) => ({ value: u.id, label: u.name }))} />}
      </Space>
      {grid && (
        <div>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {moment(grid.week_start).format('DD MMM')} - {moment(grid.week_end).format('DD MMM YYYY')}. Expected {grid.expected_hours} hours this week ({grid.hours_per_day} per working day, holidays excluded). Week start and holidays are set on the company's Settings tab.
          </Typography.Paragraph>
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            loading={loading}
            dataSource={visibleRows}
            columns={columns}
            scroll={{ x: 1100 }}
            locale={{ emptyText: 'No time on this week yet - add an engagement row below.' }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><strong>Total hrs</strong></Table.Summary.Cell>
                {grid.days.map((d, i) => (
                  <Table.Summary.Cell key={d.date} index={i + 1} align="center"><strong>{fmt(dayTotal(d.date))}</strong></Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={8} align="right">
                  <strong style={{ color: weekTotal < grid.expected_hours ? '#d46b08' : '#389e0d' }}>{fmt(weekTotal) || '0.00'}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} />
              </Table.Summary.Row>
            )}
          />
          <Space wrap style={{ marginTop: 12, width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <Select showSearch optionFilterProp="label" placeholder="Add engagement row" style={{ width: 340 }} value={adding} onChange={setAdding} options={addOptions} />
              <Button icon={<PlusOutlined />} onClick={addRow} disabled={!adding}>Add row</Button>
            </Space>
            <Space>
              <Button type="primary" icon={<SaveOutlined />} onClick={save} disabled={!dirty}>Save week</Button>
              <Popconfirm title={`Submit ${grid.draft_entries} draft entr${grid.draft_entries === 1 ? 'y' : 'ies'} of this week for approval?`} onConfirm={submit} disabled={dirty || !grid.draft_entries}>
                <Button icon={<SendOutlined />} disabled={dirty || !grid.draft_entries}>Submit week</Button>
              </Popconfirm>
            </Space>
          </Space>
          {!grid.engagements.length && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
              No open service engagements are costed to this company yet - create them under <Link to="/engagements">Workflow → Service Engagements</Link>.
            </Typography.Paragraph>
          )}
        </div>
      )}
    </div>
  );
}

/** Staff time: the weekly grid plus the flat entries list with submit / approve. */
export default function TimesheetsPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const isManager = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [timesheets, setTimesheets] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [tab, setTab] = React.useState('week');
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    request
      .get('/services')
      .then((r) => r.json())
      .then((r) => r.status && setServices(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = () => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      request.get('/timesheets', { company_id: companyId }).then((r) => r.json()),
      request.get('/clients', { company_id: companyId }).then((r) => r.json()),
      isManager ? request.get('/users', { company_id: companyId }).then((r) => r.json()) : Promise.resolve(null),
    ])
      .then(([timesheetsRes, clientsRes, usersRes]) => {
        if (timesheetsRes.status) setTimesheets(timesheetsRes.data);
        if (clientsRes.status) setClients(clientsRes.data);
        if (usersRes && usersRes.status) setUsers(usersRes.data);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchAll, [companyId]);

  const handleCreate = () => {
    form.validateFields().then((values) => {
      request
        .post('/timesheets', { ...values, company_id: companyId, work_date: values.work_date.format('YYYY-MM-DD') })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Timesheet entry added' });
            setModalOpen(false);
            fetchAll();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const act = (entry, action, okMessage) =>
    request
      .post(`/timesheets/${entry.id}/${action}`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: okMessage });
          fetchAll();
        } else {
          notification.error({ message: r.message });
        }
      });

  const columns = [
    { title: 'Date', dataIndex: 'work_date', sorter: (a, b) => (a.work_date < b.work_date ? -1 : 1), defaultSortOrder: 'descend', render: (v) => fmtDate(v) },
    ...(isManager ? [{ title: 'Staff', key: 'staff', render: (_, t) => t.User?.name || '-' }] : []),
    { title: 'Client', key: 'client', render: (_, t) => t.Client?.client_name || <Typography.Text type="secondary">Internal</Typography.Text> },
    { title: 'Service', key: 'service', render: (_, t) => t.Service?.service_name || '-' },
    { title: 'Hours', dataIndex: 'hours', align: 'right', render: (v) => number(v, 2) },
    { title: 'Rate', dataIndex: 'hourly_rate', align: 'right', render: (v) => money(v) },
    { title: 'Billable?', dataIndex: 'billable', render: (v) => (v ? 'Yes' : 'No') },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLORS[v]}>{label(v)}</Tag> },
    {
      title: '',
      key: 'actions',
      render: (_, t) => {
        const isOwn = t.user_id === auth.user.id;
        if (t.status === 'DRAFT' && (isOwn || isManager)) {
          return (
            <Button size="small" icon={<SendOutlined />} onClick={() => act(t, 'submit', 'Submitted for approval')}>
              Submit
            </Button>
          );
        }
        if (t.status === 'SUBMITTED' && isManager) {
          return (
            <Button size="small" icon={<CheckOutlined />} onClick={() => act(t, 'approve', 'Approved')}>
              Approve
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Timesheets
          </Typography.Title>
          <Typography.Text type="secondary">Weekly hours per engagement; submitted weeks go to a manager for approval and approved billable time flows onto invoices.</Typography.Text>
        </div>
        <Space>
          {isSystemAdmin && (
            <Select
              style={{ width: 200 }}
              value={companyId}
              placeholder="Company"
              onChange={setCompanyId}
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
            />
          )}
          {tab === 'list' && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                form.setFieldsValue({ work_date: moment(), billable: true });
                setModalOpen(true);
              }}
            >
              New Entry
            </Button>
          )}
        </Space>
      </Space>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'week', label: 'Weekly timesheet', children: <WeeklyGrid companyId={companyId} users={users.length ? users : [auth.user]} isManager={isManager} auth={auth} /> },
          { key: 'list', label: 'All entries', children: <Table rowKey="id" columns={columns} dataSource={timesheets} loading={loading} /> },
        ]}
      />

      <Modal title="New Timesheet Entry" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate}>
        <Form form={form} layout="vertical">
          {isManager && (
            <Form.Item name="user_id" label="Staff" tooltip="Leave blank to log time for yourself">
              <Select allowClear showSearch optionFilterProp="label" options={users.map((u) => ({ value: u.id, label: u.name }))} />
            </Form.Item>
          )}
          <Form.Item name="work_date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="client_id" label="Client" tooltip="Leave blank for internal/admin time">
            <Select allowClear showSearch optionFilterProp="label" options={clients.map((c) => ({ value: c.id, label: c.client_name }))} />
          </Form.Item>
          <Form.Item name="service_id" label="Service">
            <Select allowClear showSearch optionFilterProp="label" options={services.map((s) => ({ value: s.id, label: s.service_name }))} />
          </Form.Item>
          <Form.Item name="hours" label="Hours" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0.25} max={24} step={0.25} />
          </Form.Item>
          <Form.Item name="billable" valuePropName="checked">
            <Checkbox>Billable</Checkbox>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
